import * as React from "react";
import PropTypes from "prop-types";
import uniq from "lodash/uniq";
import shallowEqualObjects from "shallow-equal/objects";

import { EntitiesMap, EntityKey } from "../../entities";
import { SubAppState, RootState } from "../../state";
import { connect } from "react-redux";

export interface EntitiesProviderProps<E extends EntitiesMap> {
  entities: E;
  // TODO(KentoMoriwaki): Deperacate after React new Context API has arrived!
  namespace: keyof SubAppState;
}

// TODO(KentoMoriwaki): Use new Context API
export class EntitiesProvider<E extends EntitiesMap> extends React.Component<EntitiesProviderProps<E>> {
  public static childContextTypes = {
    entitiesNamespace: PropTypes.string,
  };

  public getChildContext() {
    return {
      entitiesNamespace: this.props.namespace,
    };
  }

  public render() {
    return this.props.children;
  }
}

export interface EntitiesConsumerMapper {
  [key: string]: EntityKey;
}

export interface ConsumerOwnProps<P, M extends EntitiesConsumerMapper> {
  query: { [key in keyof M]: EntityId | EntityId[] | undefined };
  children(param: Partial<P>): React.ReactNode;
}

export interface ConsumerMappedProps {
  entities: Partial<EntitiesMap>;
}

export type ConsumerProps<P, M extends EntitiesConsumerMapper> = ConsumerOwnProps<P, M> & ConsumerMappedProps;

export function createEntitiesConsumer<P>(mapper: EntitiesConsumerMapper) {
  const entityKeys = uniq(Object.keys(mapper)) as EntityKey[];
  const newContextAPI = false;

  type Props = ConsumerProps<P, typeof mapper>;

  // tslint:disable-next-line:max-classes-per-file
  class EntitiesConsumer extends React.Component<Props> {
    public shouldComponentUpdate(nextProps: Props) {
      if (!newContextAPI) {
        // TODO(KentoMoriwaki): Old Context API cannot emit context change when shouldComponentUpdate returns false
        return true;
      }

      if (this.props.children !== nextProps.children) {
        return true;
      }
      if (this.props.query !== nextProps.query && !shallowEqualObjects(this.props.query, nextProps.query)) {
        return true;
      }
      let entitiesChanged = this.props.entities !== nextProps.entities;
      if (entitiesChanged) {
        entitiesChanged = entityKeys.some(key => {
          return this.props.entities[key] !== nextProps.entities[key];
        });
      }
      return entitiesChanged;
    }

    public render() {
      const { query, children, entities } = this.props;
      const res = {} as any;
      Object.entries(query).forEach(([key, ids]) => {
        const ent = entities[mapper[key]]!;
        if (Array.isArray(ids)) {
          res[key] = ids.map(id => ent[id]);
        } else if (ids) {
          res[key] = ent[ids];
        }
      });
      return children(res) || null;
    }
  }

  // TODO(KentoMoriwaki): Stop using connect and use Context API

  function mapStateToProps(state: RootState, ownProps: { namespace: keyof SubAppState }): ConsumerMappedProps {
    return {
      entities: state[ownProps.namespace].entities,
    };
  }

  const ConnectedEntitiesConsumer = connect(mapStateToProps)(EntitiesConsumer);

  type OwnProps = ConsumerOwnProps<P, typeof mapper>;

  // tslint:disable-next-line:max-classes-per-file
  class EntitiesConsumerWrapper extends React.Component<OwnProps> {
    public static contextTypes = {
      entitiesNamespace: PropTypes.string,
    };

    // Use for type hinting
    public static children: OwnProps["children"];

    public render() {
      return <ConnectedEntitiesConsumer {...this.props} namespace={this.context.entitiesNamespace} />;
    }
  }

  return EntitiesConsumerWrapper;
}



// WEBPACK FOOTER //
// ./frontend/src/lib/entities/EntitiesContext.tsx